import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import CustomSelect from '../common/CustomSelect';
import type { TimePeriod, TimePeriodSelectorProps } from '../../types/analytics';

/**
 * Time period options with display labels
 */
const PERIOD_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

/**
 * Format date for display (e.g., "Jan 1")
 * Handles date-only strings (YYYY-MM-DD) by treating them as local dates
 */
function formatDateShort(dateString: string): string {
  try {
    // Parse YYYY-MM-DD format as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
}

/**
 * TimePeriodSelector Component
 *
 * Allows users to select a time period for analytics filtering.
 * Supports preset periods (Today, This Week, etc.) and custom date ranges.
 */
const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({
  selectedPeriod,
  customStartDate,
  customEndDate,
  onChange,
}) => {
  // Local state for custom dates (not submitted until Apply clicked)
  const [localStartDate, setLocalStartDate] = useState(customStartDate || '');
  const [localEndDate, setLocalEndDate] = useState(customEndDate || '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showCustomInputs, setShowCustomInputs] = useState(selectedPeriod === 'custom');

  // Sync local state when props change
  useEffect(() => {
    if (customStartDate) setLocalStartDate(customStartDate);
    if (customEndDate) setLocalEndDate(customEndDate);
  }, [customStartDate, customEndDate]);

  // Show/hide custom inputs based on selected period
  useEffect(() => {
    setShowCustomInputs(selectedPeriod === 'custom');
  }, [selectedPeriod]);

  /**
   * Handle period dropdown change
   */
  const handlePeriodChange = (value: string) => {
    const newPeriod = value as TimePeriod;
    setValidationError(null);

    if (newPeriod === 'custom') {
      // Show custom inputs but don't trigger onChange until Apply
      setShowCustomInputs(true);
    } else {
      // For preset periods, trigger onChange immediately
      setShowCustomInputs(false);
      onChange(newPeriod, undefined, undefined);
    }
  };

  /**
   * Validate and apply custom date range
   */
  const handleApplyCustomDates = () => {
    setValidationError(null);

    // Validate start date
    if (!localStartDate) {
      setValidationError('Start date is required');
      return;
    }

    // Validate end date
    if (!localEndDate) {
      setValidationError('End date is required');
      return;
    }

    // Validate date range
    const start = new Date(localStartDate);
    const end = new Date(localEndDate);

    if (end < start) {
      setValidationError('End date must be after start date');
      return;
    }

    // All valid - trigger onChange
    onChange('custom', localStartDate, localEndDate);
  };

  /**
   * Get display label for custom date range
   */
  const getCustomDateLabel = () => {
    if (customStartDate && customEndDate) {
      return `Custom: ${formatDateShort(customStartDate)} - ${formatDateShort(customEndDate)}`;
    }
    return null;
  };

  return (
    <div data-testid="time-period-selector" className="flex flex-wrap items-center gap-4">
      {/* Period Dropdown */}
      <div className="flex items-center gap-2">
        <label htmlFor="period-select" className="sr-only">
          Period
        </label>
        <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <CustomSelect
          value={selectedPeriod}
          onChange={handlePeriodChange}
          options={PERIOD_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          aria-label="Period"
        />
      </div>

      {/* Custom Date Range Display */}
      {selectedPeriod === 'custom' && customStartDate && customEndDate && !showCustomInputs && (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {getCustomDateLabel()}
        </span>
      )}

      {/* Show custom range label when custom period has dates */}
      {selectedPeriod === 'custom' && customStartDate && customEndDate && (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateShort(customStartDate)} - {formatDateShort(customEndDate)}
        </span>
      )}

      {/* Custom Date Inputs */}
      {showCustomInputs && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <label htmlFor="start-date" className="text-sm text-gray-600 dark:text-gray-400">
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              aria-label="Start Date"
              value={localStartDate}
              onChange={(e) => {
                setLocalStartDate(e.target.value);
                setValidationError(null);
              }}
              className="block rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-2 px-3 bg-white dark:bg-cap-dark border"
            />
          </div>

          <span className="text-gray-400">to</span>

          <div className="flex items-center gap-2">
            <label htmlFor="end-date" className="text-sm text-gray-600 dark:text-gray-400">
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              aria-label="End Date"
              value={localEndDate}
              onChange={(e) => {
                setLocalEndDate(e.target.value);
                setValidationError(null);
              }}
              className="block rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm py-2 px-3 bg-white dark:bg-cap-dark border"
            />
          </div>

          <button
            type="button"
            onClick={handleApplyCustomDates}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Apply
          </button>
        </div>
      )}

      {/* Validation Error */}
      {validationError && (
        <div role="alert" className="text-sm text-red-600">
          {validationError}
        </div>
      )}

      {/* Quick Select Buttons (Optional Enhancement) */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            // Calculate 7 days ago
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 7);
            onChange('custom', start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
          }}
          className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          7D
        </button>
        <button
          type="button"
          onClick={() => {
            // Calculate 30 days ago
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 30);
            onChange('custom', start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
          }}
          className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          30D
        </button>
        <button
          type="button"
          onClick={() => {
            // Calculate 90 days ago
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 90);
            onChange('custom', start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
          }}
          className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          90D
        </button>
      </div>
    </div>
  );
};

export default TimePeriodSelector;
